<template>
  <!-- Minimized: compact floating bar -->
  <div v-if="minimized" class="pdf-search-compact" @click="emit('expand')">
    <v-icon :icon="mdiMagnify" size="16" class="pdf-search-compact__icon" />
    <span class="pdf-search-compact__query">{{ localQuery }}</span>
    <template v-if="searchMode !== 'none'">
      <span class="pdf-search-compact__counter">
        {{ currentIndex + 1 }}/{{ navigableCount }}
      </span>
      <div class="pdf-search-compact__arrows" @click.stop>
        <v-btn
          :icon="mdiChevronUp"
          variant="text"
          size="x-small"
          density="compact"
          @click="emit('prevResult')"
        />
        <v-btn
          :icon="mdiChevronDown"
          variant="text"
          size="x-small"
          density="compact"
          @click="emit('nextResult')"
        />
      </div>
    </template>
    <v-btn
      :icon="mdiClose"
      variant="text"
      size="x-small"
      density="compact"
      @click.stop="handleClose"
    />
  </div>

  <!-- Expanded: full search panel -->
  <div v-else class="pdf-search-panel">
    <!-- Header -->
    <div class="pdf-search-panel__header">
      <span class="pdf-search-panel__title">{{ t('search.title') }}</span>
      <div class="pdf-search-panel__header-actions">
        <v-btn
          :icon="mdiMinus"
          variant="text"
          size="x-small"
          density="compact"
          @click="emit('minimize')"
        />
        <v-btn
          :icon="mdiClose"
          variant="text"
          size="x-small"
          density="compact"
          @click="handleClose"
        />
      </div>
    </div>

    <!-- Input -->
    <div class="pdf-search-panel__input-wrapper">
      <v-text-field
        ref="inputRef"
        v-model="localQuery"
        :placeholder="placeholder || t('search.placeholder')"
        variant="solo-filled"
        flat
        density="compact"
        hide-details
        clearable
        :prepend-inner-icon="mdiMagnify"
        @click:clear="handleClear"
      />
    </div>

    <!-- Navigation (compact inline row) -->
    <div v-if="searchMode !== 'none' || isSearching" class="pdf-search-panel__nav">
      <v-progress-circular
        v-if="isSearching"
        indeterminate
        size="16"
        width="2"
        color="primary"
      />
      <template v-else>
        <span class="pdf-search-panel__counter">
          {{ currentIndex + 1 }} / {{ navigableCount }}
        </span>
        <div class="pdf-search-panel__nav-arrows">
          <v-btn
            :icon="mdiChevronUp"
            variant="text"
            size="x-small"
            density="compact"
            @click="emit('prevResult')"
          />
          <v-btn
            :icon="mdiChevronDown"
            variant="text"
            size="x-small"
            density="compact"
            @click="emit('nextResult')"
          />
        </div>
      </template>
    </div>

    <!-- Suggestion list -->
    <div v-if="hasSuggestions" class="pdf-search-panel__suggestions">
      <div
        v-for="suggestion in suggestions"
        :key="suggestion.isViewAll ? '__view_all__' : suggestion.word"
        class="pdf-search-panel__suggestion-item"
        :class="{
          'pdf-search-panel__suggestion-item--active': suggestion.isViewAll && searchMode === 'all',
          'pdf-search-panel__suggestion-item--view-all': suggestion.isViewAll,
        }"
        @click="handleSuggestionClick(suggestion)"
      >
        <span class="pdf-search-panel__suggestion-word">
          {{ suggestion.isViewAll ? t('search.viewAll') : suggestion.word }}
        </span>
        <v-chip size="x-small" variant="tonal" color="primary">
          {{ suggestion.count }}
        </v-chip>
      </div>
    </div>

    <!-- No results -->
    <div
      v-else-if="debouncedQuery.trim() && !isSearching"
      class="pdf-search-panel__empty"
    >
      {{ t('search.noResults') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { refDebounced } from '@vueuse/core'
import { mdiMagnify, mdiMinus, mdiChevronUp, mdiChevronDown, mdiClose } from '@mdi/js'
import type { PDFSearchSuggestion, PDFSearchMode } from '../types'
import { usePDFI18n } from '../i18n'

const { t } = usePDFI18n()

const props = withDefaults(
  defineProps<{
    query: string
    suggestions: PDFSearchSuggestion[]
    navigableCount: number
    currentIndex: number
    isSearching: boolean
    searchMode: PDFSearchMode
    minimized: boolean
    placeholder?: string
  }>(),
  {
    placeholder: undefined,
  },
)

const emit = defineEmits<{
  search: [query: string]
  clear: []
  close: []
  minimize: []
  expand: []
  prevResult: []
  nextResult: []
  selectViewAll: []
  selectWord: [word: string]
}>()

const localQuery = ref(props.query)
const debouncedQuery = refDebounced(localQuery, 300)
const inputRef = ref()

// Prevents the debounced watcher from re-emitting search when selectWord sets the query
let suppressNextSearch = false

const hasSuggestions = computed(() => props.suggestions.length > 0)

watch(
  () => props.query,
  (newQuery) => {
    if (newQuery !== localQuery.value) {
      suppressNextSearch = true
      localQuery.value = newQuery
    }
  },
)

watch(debouncedQuery, (newQuery) => {
  if (suppressNextSearch) {
    suppressNextSearch = false
    return
  }
  if (newQuery.trim()) {
    emit('search', newQuery)
  } else {
    emit('clear')
  }
})

// Focus input when expanding from minimized
watch(
  () => props.minimized,
  (minimized) => {
    if (!minimized) {
      nextTick(() => {
        const el = inputRef.value?.$el?.querySelector('input') as HTMLInputElement | null
        el?.focus()
      })
    }
  },
)

onMounted(() => {
  if (!props.minimized) {
    nextTick(() => {
      const el = inputRef.value?.$el?.querySelector('input') as HTMLInputElement | null
      el?.focus()
    })
  }
})

function handleSuggestionClick(suggestion: PDFSearchSuggestion): void {
  if (suggestion.isViewAll) {
    emit('selectViewAll')
  } else {
    emit('selectWord', suggestion.word)
  }
}

function handleClear(): void {
  localQuery.value = ''
  emit('clear')
}

function handleClose(): void {
  handleClear()
  emit('close')
}
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

// ─── Compact minimized bar ─────────────────────────────────────────────────
.pdf-search-compact {
  position: absolute;
  top: $space-sm;
  right: $space-sm;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: $space-xs;
  padding: $space-xs $space-sm;
  background: rgb(var(--v-theme-surface));
  border-radius: $radius-full;
  box-shadow: $shadow-md;
  cursor: pointer;
  max-width: 340px;

  @media (max-width: 600px) {
    position: relative;
    top: 0;
    right: 0;
    max-width: 100%;
    border-radius: 0;
    box-shadow: none;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  &:hover {
    box-shadow: $shadow-lg;
  }

  &__icon {
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    flex-shrink: 0;
  }

  &__query {
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__counter {
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    white-space: nowrap;
    flex-shrink: 0;
  }

  &__arrows {
    display: flex;
    flex-shrink: 0;
  }
}

// ─── Full expanded panel ────────────────────────────────────────────────────
.pdf-search-panel {
  position: absolute;
  top: $space-sm;
  right: $space-sm;
  z-index: 10;
  width: 340px;
  max-height: 480px;

  @media (max-width: 600px) {
    position: relative;
    top: 0;
    right: 0;
    width: 100%;
    max-height: 320px;
    border-radius: 0;
    box-shadow: none;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }
  background: rgb(var(--v-theme-surface));
  border-radius: $radius-lg;
  box-shadow: $shadow-lg;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $space-md $space-md $space-xs;
  }

  &__header-actions {
    display: flex;
    gap: 2px;
  }

  &__title {
    font-size: $text-md;
    font-weight: $font-medium;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  }

  &__input-wrapper {
    padding: 0 $space-md $space-sm;
  }

  &__nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 $space-md $space-sm;
    min-height: 28px;
  }

  &__counter {
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
    white-space: nowrap;
  }

  &__nav-arrows {
    display: flex;
    gap: 2px;
  }

  &__suggestions {
    flex: 1;
    overflow-y: auto;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(var(--v-theme-primary), 0.3);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(var(--v-theme-primary), 0.5);
    }
  }

  &__suggestion-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $space-sm $space-lg;
    cursor: pointer;
    transition: background-color $duration-fast $ease-default;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }

    &--view-all {
      font-weight: $font-semibold;
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.08);

      .pdf-search-panel__suggestion-word {
        color: rgb(var(--v-theme-primary));
      }
    }
  }

  &__suggestion-word {
    font-size: $text-base;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__empty {
    padding: $space-xl $space-lg;
    text-align: center;
    font-size: $text-sm;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
  }
}
</style>
